import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable, Subject } from "rxjs";
import { Picture } from "src/models/picture";
import { HttpRequest, HttpEvent, HttpEventType } from "@angular/common/http";
import { map, tap, last, finalize } from "rxjs/operators";
import { HttpExtendedService } from "./http-extended.service";
import { ConfigService } from "./config.service";

@Injectable({
  providedIn: "root",
})
export class PictureService {
  private isPreview: boolean;
  previewCast: Subject<boolean> = new Subject();
  apiUrl: string;

  private _pictures = new BehaviorSubject<Picture[]>([]);
  _picturesCast = this._pictures.asObservable();

  private _currentPicture = new BehaviorSubject<Picture>(null);
  _pictureCast = this._currentPicture.asObservable();

  private dataStore: {
    pictures: Picture[];
    picturesByCategory: Picture[];
  };

  constructor(
    protected http: HttpExtendedService,
    // private toast: ToastrService,
    private config: ConfigService
  ) {
    this.apiUrl = this.config.config.pictureUrl;
    this.dataStore = { pictures: [], picturesByCategory: [] };
    this.previewCast.subscribe((p) => {
      this.isPreview = p;
    });
  }

  private assign() {
    this._pictures.next(Object.assign({}, this.dataStore).pictures);
  }

  preview() {
    this.previewCast.next(!this.isPreview);
  }

  getCurrentPictureIndex() {
    return this.dataStore.pictures.indexOf(this._currentPicture.getValue()) + 1;
  }

  addPicture(picture: Picture, progressCallback: any, endCallBack: any) {
    const formData = new FormData();
    formData.append("file", picture.file);
    delete picture.file;
    formData.append("picture", JSON.stringify(picture));

    const req = new HttpRequest(
      "POST",
      this.config.config.apiUrl + this.apiUrl,
      formData,
      { reportProgress: true }
    );
    this.http
      .request(req)
      .pipe(
        map((event) => this.getEventMessage(event, picture)),
        tap((message) => progressCallback(message)),
        last(progressCallback("Picture saved"))
      )
      .subscribe(
        (data: Picture) => {
          this.dataStore.pictures.push(data);
        },
        (err) => console.error(err),
        () => {
          this.assign();
          // this.toast.success('Picture saved!');
          endCallBack();
        }
      );
  }

  getPictures() {
    this.http.get<Picture[]>(this.apiUrl).subscribe(
      (pictures) => (this.dataStore.pictures = pictures),
      (err) => console.error(err),
      () => this.assign()
    );
  }

  getPicturesByCategory(categoryId: string) {
    this.http.get<Picture[]>(this.apiUrl + "/category/" + categoryId).subscribe(
      (pictures) =>
        (this.dataStore.pictures = pictures.sort((a, b) => a.order - b.order)),
      (err) => console.error(err),
      () => this.assign()
    );
  }

  deletePicture(picture: Picture, endCallback: any) {
    this.http.post<Picture>(this.apiUrl + "/delete", picture).subscribe(
      () => {
        const index = this.dataStore.pictures.indexOf(picture);
        this.dataStore.pictures.splice(index, 1);
      },
      (err) => console.error(err),
      () => {
        this.assign();
        // this.toast.success('Picture Deleted!');
        endCallback();
      }
    );
  }

  removePicture(picture: Picture): Observable<boolean> {
    if (!picture) {
      return new Observable<boolean>((observer) => {
        observer.next(true);
      });
    }
    return this.http.post(this.apiUrl + "/delete", picture);
  }

  updatePicture(picture: Picture, endCallback: any) {
    let pictureToUpdate = this.dataStore.pictures.find(
      (x) => x.id === picture.id
    );

    this.http.put<Picture>(this.apiUrl, picture).subscribe(
      (p: Picture) => (pictureToUpdate = p),
      (err) => console.error(err),
      () => {
        // this.toast.success('Picture updated!');
        this.assign();
        endCallback();
      }
    );
  }

  getPicturesForDiscover(discoverId: string): Observable<void> {
    return this.http
      .get<Picture[]>(this.apiUrl + "/discover/" + discoverId)
      .pipe(
        map((pictures) => {
          this.dataStore.pictures = pictures.sort((a, b) => a.order - b.order);
        }),
        finalize(() => this.assign())
      );
  }

  getMainPictureForDiscover(discoverId: string): Observable<Picture> {
    return this.http
      .get<Picture[]>(this.apiUrl + "/discover/ " + discoverId)
      .pipe(
        map((x) => {
          const orders = x.map((y) => y.order);
          const minOrder = Math.min(...orders);
          return x.find((y) => y.order === minOrder);
        })
      );
  }

  getCurrentPicture(pictureId: string) {
    const picture = this.dataStore.pictures.find((x) => x.id === pictureId);
    this._currentPicture.next(picture);
  }

  getNextPicture() {
    const index =
      this.dataStore.pictures.indexOf(this._currentPicture.getValue()) + 1;
    if (index >= this.dataStore.pictures.length) {
      return;
    }
    this._currentPicture.next(this.dataStore.pictures[index]);
  }

  getPreviousPicture() {
    const index =
      this.dataStore.pictures.indexOf(this._currentPicture.getValue()) - 1;
    if (index < 0) {
      return;
    }
    this._currentPicture.next(this.dataStore.pictures[index]);
  }

  isLastPicture() {
    return (
      this._currentPicture.getValue().id ===
      this.dataStore.pictures[this.dataStore.pictures.length - 1].id
    );
  }

  isFirstPicture() {
    return this._currentPicture.getValue().id === this.dataStore.pictures[0].id;
  }

  getNumberOfPictures() {
    return this.dataStore.pictures.length;
  }

  getPictureForNews(newsId: string): Observable<Picture> {
    return this.http.get<Picture>(this.apiUrl + "/news/" + newsId).pipe(
      map((x) => {
        return x;
      })
    );
  }

  private getEventMessage(event: HttpEvent<any>, picture: Picture): any {
    switch (event.type) {
      case HttpEventType.Sent:
        return 0;

      case HttpEventType.UploadProgress:
        // Compute and show the % done:
        const percentDone = Math.round((100 * event.loaded) / event.total);
        return percentDone;

      case HttpEventType.Response:
        return event.body;

      default:
        return 0;
    }
  }
}
